"use client";

import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

export interface TocEntry {
  id: string;
  text: string;
}

function RailLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-text-tertiary block font-mono text-[11px] font-medium tracking-[2px] uppercase">
      {children}
    </span>
  );
}

export function ArticleRail({ toc }: { toc: TocEntry[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!toc.length) return;
    const headings = toc
      .map((entry) => document.getElementById(entry.id))
      .filter((el): el is HTMLElement => el !== null);
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-20% 0px -65% 0px" },
    );
    headings.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [toc]);

  return (
    // Always rendered, even with an empty toc: the parent grid places children
    // implicitly, so returning null would drop the article body into the
    // 240px rail column.
    <aside className="flex flex-col self-start lg:sticky lg:top-28">
      {toc.length > 0 && (
        <div>
          <RailLabel>On this page</RailLabel>
          <nav className="mt-1.5 flex flex-col">
            {toc.map((entry) => (
              <a
                key={entry.id}
                href={`#${entry.id}`}
                className={cn(
                  "text-sm leading-6 no-underline transition-colors",
                  activeId === entry.id
                    ? "text-foreground"
                    : "text-text-secondary hover:text-foreground",
                )}
              >
                {entry.text}
              </a>
            ))}
          </nav>
        </div>
      )}
    </aside>
  );
}
